package com.iregained.site.components.sections.product.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.clinicians.sections.ProcurementOptionData
import com.iregained.site.components.sections.clinicians.sections.defaultCliniciansProcurementData
import com.iregained.site.components.sections.corporate.styles.ActionButtonStyle
import com.iregained.site.components.sections.corporate.styles.AudienceToggleTabStyle
import com.iregained.site.components.sections.corporate.styles.ProcurementCardStyle
import com.iregained.site.components.sections.patients.sections.PatientPricingOption
import com.iregained.site.components.sections.patients.sections.defaultPatientsPricingData
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Elastic Pop
@Composable
private fun Modifier.elasticPop(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.8f).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun ProductProcurementSection(data: ProductProcurementSectionData = defaultProductProcurementData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "product-procurement-section"
    var showB2bOptions by remember { mutableStateOf(true) }
    var isVisible by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.custom.lightGray)
            .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
            .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center).toAttrs {
                ref { element ->
                    val observer = IntersectionObserver { entries, obs ->
                        entries.forEach {
                            if (it.isIntersecting) {
                                isVisible = true; obs.unobserve(element)
                            }
                        }
                    }
                    observer.observe(element); onDispose { observer.disconnect() }
                }
            }) {
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(1440.px), horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (data.sectionHeader.isNotBlank()) {
                    SectionHeader(
                        text = data.sectionHeader,
                        fontSize = 32.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 24.px).elasticPop(isVisible, 0),
                        alignment = Alignment.CenterHorizontally
                    )
                }

                Row(
                    modifier = Modifier.margin(bottom = 40.px).borderRadius(30.px).backgroundColor(Colors.White)
                        .padding(4.px).elasticPop(isVisible, 150), verticalAlignment = Alignment.CenterVertically
                ) {
                    ToggleTab(
                        text = "Clinics & Facilities",
                        isActive = showB2bOptions,
                        onClick = { showB2bOptions = true },
                        palette = palette
                    )
                    ToggleTab(
                        text = "Patients & Caregivers",
                        isActive = !showB2bOptions,
                        onClick = { showB2bOptions = false },
                        palette = palette
                    )
                }

                SimpleGrid(
                    numColumns = numColumns(base = 1, lg = 3),
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px).gap(30.px)
                ) {
                    if (showB2bOptions) {
                        data.b2bOptions.forEachIndexed { index, it ->
                            key(it.title) {
                                SharedProcurementCard(
                                    it.title,
                                    it.badgeText,
                                    it.targetProfile,
                                    it.coreAdvantage,
                                    it.inclusions,
                                    it.buttonText,
                                    it.linkPath,
                                    it.isFeatured,
                                    true,
                                    Modifier.elasticPop(isVisible, 300 + (index * 150))
                                )
                            }
                        }
                    } else {
                        data.b2cOptions.forEachIndexed { index, it ->
                            key(it.title) {
                                SharedProcurementCard(
                                    it.title,
                                    it.badge,
                                    it.targetProfile,
                                    it.advantage,
                                    it.inclusions,
                                    it.buttonText,
                                    it.linkPath,
                                    it.isFeatured,
                                    false,
                                    Modifier.elasticPop(isVisible, 300 + (index * 150))
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ToggleTab(
    text: String, isActive: Boolean, onClick: () -> Unit, palette: com.iregained.site.theme.SitePalette
) {
    Box(
        modifier = AudienceToggleTabStyle.toModifier().borderRadius(26.px)
            .backgroundColor(if (isActive) palette.brand.primary else Colors.Transparent)
            .padding(topBottom = 10.px, leftRight = 24.px).cursor(Cursor.Pointer).onClick { onClick() },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            text = text,
            modifier = Modifier.color(if (isActive) Colors.White else palette.custom.darkText).fontSize(16.px)
                .fontWeight(if (isActive) FontWeight.Bold else FontWeight.Medium)
        )
    }
}

@Composable
private fun SharedProcurementCard(
    title: String,
    badge: String,
    profile: String,
    advantage: String,
    inclusions: List<String>,
    btnText: String,
    path: String,
    isFeatured: Boolean,
    useGraphicalBullets: Boolean,
    modifier: Modifier = Modifier
) {
    val palette = ColorMode.current.toSitePalette()
    val cardMod = ProcurementCardStyle.toModifier().then(modifier).fillMaxWidth().minHeight(540.px).borderRadius(12.px)
        .backgroundColor(Colors.White)
        .boxShadow(0.px, 8.px, 24.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.06f))
        .thenIf(isFeatured, Modifier.border(2.px, LineStyle.Solid, palette.brand.primary))
    Box(modifier = cardMod) {
        Column(
            modifier = Modifier.fillMaxSize().padding(32.px),
            verticalArrangement = Arrangement.SpaceBetween,
            horizontalAlignment = Alignment.Start
        ) {
            Column(modifier = Modifier.fillMaxWidth()) {
                if (badge.isNotBlank()) Box(
                    modifier = Modifier.margin(bottom = 16.px).borderRadius(16.px).backgroundColor(
                        if (isFeatured) palette.brand.primarySoft else palette.custom.darkText.toRgb()
                            .copyf(alpha = 0.1f)
                    ).padding(topBottom = 4.px, leftRight = 14.px)
                ) {
                    SpanText(
                        text = badge,
                        modifier = Modifier.color(if (isFeatured) palette.brand.primary else palette.custom.darkText)
                            .fontSize(13.px).fontWeight(FontWeight.Bold)
                    )
                }
                SpanText(
                    text = title,
                    modifier = Modifier.color(palette.brand.primary).fontWeight(FontWeight.Bold).fontSize(25.px)
                        .margin(bottom = 12.px)
                )
                SpanText(
                    text = profile,
                    modifier = Modifier.color(palette.custom.darkText).fontSize(18.px).lineHeight(1.4)
                        .fontWeight(FontWeight.Medium).margin(bottom = 16.px)
                )
                Box(
                    modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(palette.custom.divider)
                        .margin(bottom = 16.px)
                )
                Div(
                    attrs = Modifier.color(palette.custom.darkText).fontSize(18.px).lineHeight(1.5)
                        .margin(bottom = 20.px).toAttrs { ref { el -> el.innerHTML = advantage; onDispose {} } })
                Column(modifier = Modifier.gap(12.px).margin(bottom = 24.px)) {
                    inclusions.forEach { itemHtml ->
                        key(itemHtml) {
                            Row(verticalAlignment = Alignment.Top) {
                                if (useGraphicalBullets) Box(
                                    modifier = Modifier.margin(top = 7.px, right = 10.px).size(6.px).minSize(6.px)
                                        .flexShrink(0).borderRadius(50.percent).backgroundColor(palette.brand.primary)
                                )
                                else SpanText(
                                    text = "✓",
                                    modifier = Modifier.color(palette.brand.primary).fontWeight(FontWeight.Bold)
                                        .margin(right = 8.px)
                                )
                                Div(
                                    attrs = Modifier.color(palette.custom.darkText).fontSize(18.px).lineHeight(1.3)
                                        .toAttrs { ref { el -> el.innerHTML = itemHtml; onDispose {} } })
                            }
                        }
                    }
                }
            }
            if (btnText.isNotBlank() && path.isNotBlank()) Link(
                path = path, modifier = Modifier.fillMaxWidth().textDecorationLine(TextDecorationLine.None)
            ) {
                Box(
                    modifier = ActionButtonStyle.toModifier().fillMaxWidth().borderRadius(8.px)
                        .backgroundColor(palette.brand.primary).padding(topBottom = 12.px).cursor(Cursor.Pointer),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        text = btnText,
                        modifier = Modifier.color(Colors.White).fontSize(20.px).fontWeight(FontWeight.SemiBold)
                    )
                }
            }
        }
    }
}

// --- CMS DATA MODEL ---
data class ProductProcurementSectionData(
    val sectionId: String? = null,
    val sectionHeader: String,
    val b2bOptions: List<ProcurementOptionData>,
    val b2cOptions: List<PatientPricingOption>
)

// --- AGGREGATOR SWITCHBOARD ---
val defaultProductProcurementData: ProductProcurementSectionData
    get() = ProductProcurementSectionData(
        sectionId = "product-procurement-section", sectionHeader = "FLEXIBLE PROCUREMENT & ACCESS PATHWAYS",
        // Automatically fetches the correct region data based on the switchboards in their respective files!
        b2bOptions = defaultCliniciansProcurementData.options, b2cOptions = defaultPatientsPricingData.options
    )